import React, { useEffect, useRef, useState } from "react";
import OTPInput from "../../../(components)/ui/otpInput";
import { useFormik, FormikHelpers } from "formik";
import ToastSuccess from "../../../../(common)/Toast/ToastSuccess";
import ToastWarning from "../../../../(common)/Toast/ToastWarning";
import {
  StyledButton,
  buttonStyles,
} from "../../../../../Shared/button/button";
import { ACCENTCOLOR } from "../../../../../Shared/colors";
import { TEXTS } from "../../../constants/texts";
import { callApi } from "../../../lib/api";
import {
  resendSignupOtp,
  validateSignupOtp,
} from "../../../../../utils/otpApi";
import { otpSchema } from "../../../constants/schema";
import { testIdProps } from "../../../../../utils/testIdProps";

export interface FormValuesStep2 {
  verificationCode: string;
  email: string;
}

const Step2 = ({
  createdUserData,
  setCreatedUserData,
  setStep,
  step,
  token = createdUserData.token, // Default to createdUserData.token if not passed
  relayId = createdUserData.relayId, // Default to createdUserData.relayId if not passed
  email = createdUserData.email, // Default to createdUserData.email if not passed
}: {
  createdUserData: {
    relayId: string;
    email: string;
    token: string;
  };
  setCreatedUserData: React.Dispatch<React.SetStateAction<any>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  step: number;
  token: string;
  relayId: string;
  email: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const resendingRef = useRef(false);

  const formik = useFormik<FormValuesStep2>({
    initialValues: {
      verificationCode: "",
      email: "", // Pass from props if needed
    },
    validationSchema: otpSchema,
    onSubmit: async (
      values: FormValuesStep2,
      { setSubmitting }: FormikHelpers<FormValuesStep2>
    ) => {
      await handleVerifyOtp(values);
      setSubmitting(false);
    },
    validateOnBlur: true,
  });

  const handleResendEmail = async () => {
    if (countdown !== 0 || resendingRef.current) {
      return;
    }

    resendingRef.current = true;
    setCountdown(30);

    try {
      const res = await resendSignupOtp(callApi, email, token);

      setCreatedUserData({
        ...createdUserData,
        relayId: res.relay_id,
        token: res.token || token,
      });
      ToastSuccess(createdUserData.email, [
        TEXTS.SIGNUP_OTP_RESEND,
        TEXTS.SIGNUP_OTP_MESSAGE,
      ]);
    } catch (err: any) {
      ToastWarning("", err.message);
    } finally {
      resendingRef.current = false;
    }
  };

  const handleVerifyOtp = async (values: FormValuesStep2) => {
    if (values.verificationCode.length < 6) {
      return;
    }

    try {
      setLoading(true);
      await validateSignupOtp(callApi, {
        email,
        relayId,
        otp: values.verificationCode,
        signupToken: token,
      });

      ToastSuccess("", [
        TEXTS.SIGNUP_OTP_VERIFIED,
        TEXTS.SIGNUP_OTP_VERIFIED_MESSAGE,
      ]);

      setStep(3);
      // setOtpInputs(Array(6).fill(""));
      formik.setFieldValue("verificationCode", "");
    } catch (err: any) {
      ToastWarning("", err.message);
      // setOtpInputs(Array(6).fill(""));
      formik.setFieldValue("verificationCode", "");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (step === 2 && countdown > 0) {
      timerId = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [step, countdown]);

  // Start countdown only once when entering Step 2
  useEffect(() => {
    if (step === 2 && countdown === 0) {
      setCountdown(30);
    }
  }, [step]);

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="d-flex flex-column shadow bg-white border rounded-4 px-4 py-4"
      style={{
        width: 400,
        boxShadow:
          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
        transition: "width 0.3s ease", // optional: smooth transition
      }}
    >
      <div className="d-flex align-items-center gap-2">
        <button
          {...testIdProps("signup-otp-back")}
          type="button"
          onClick={() => setStep(1)}
          style={{ border: "none", background: "transparent" }}
        >
          {"<"}
        </button>
        <h2 className="text-2xl fw-semibold">Verify your email</h2>
      </div>
      <p
        style={{ color: "#A1A1A1", fontSize: "14px" }}
        className="m-0 pb-4 mt-2"
      >
        Enter the 6-digit code sent to{" "}
        <strong className="" style={{ color: "#232323", fontSize: "14px" }}>
          {email}
        </strong>
      </p>

      <OTPInput
        idPrefix="signup-otp"
        length={6}
        onChange={(code) => {
          formik.setFieldValue("verificationCode", code);
        }}
      />

      <div className="text-sm justify-content-end my-3 align-items-center d-flex gap-2">
        {countdown > 0 ? (
          <>
            Resend in<span className="text-danger">{countdown}s</span>
          </>
        ) : (
          <button
            {...testIdProps("signup-otp-resend")}
            style={{
              backgroundColor: "transparent",
              color: ACCENTCOLOR,
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontWeight: "500",
              fontSize: "14px",
              textAlign: "center",
              textDecoration: "underline",
            }}
            type="button"
            className="p-0 text-decoration-none"
            onClick={() => handleResendEmail()}
          >
            Resend OTP
          </button>
        )}
      </div>

      <StyledButton
        id="signup-otp-verify"
        type="submit"
        style={
          !formik.isValid ||
          !formik.dirty ||
          formik.isSubmitting ||
          formik.values.verificationCode.length !== 6
            ? buttonStyles.disabled.normal
            : buttonStyles.primary.normal
        }
        hoverStyle={
          !formik.isValid ||
          !formik.dirty ||
          formik.isSubmitting ||
          formik.values.verificationCode.length !== 6
            ? buttonStyles.disabled.hover
            : buttonStyles.primary.hover
        }
        disabled={
          formik.isSubmitting ||
          loading ||
          !formik.isValid ||
          formik.values.verificationCode.length !== 6
        }
        isLoading={loading}
      >
        Verify
      </StyledButton>
    </form>
  );
};

export default Step2;
